import React from 'react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1); // 1..12
const STEP_MINUTES = Array.from({ length: 12 }, (_, i) => i * 5); // 0,5,...,55

function parse24(value) {
  if (!value || !value.includes(':')) return { h12: '', minute: '', period: '' };
  const [hStr, mStr] = value.split(':');
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return { h12: '', minute: '', period: '' };
  const period = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return { h12: String(h), minute: String(m).padStart(2, '0'), period };
}

function to24(h12, minute, period) {
  let h = parseInt(h12, 10) % 12;
  if (period === 'PM') h += 12;
  return `${String(h).padStart(2, '0')}:${minute}`;
}

/**
 * 12-hour time picker (hour / minute / AM-PM) backed by shadcn Select.
 * Stores/returns value as 24-hour 'HH:MM' for backend compatibility.
 */
export const TimePicker = ({ value, onChange, testid }) => {
  const { h12, minute, period } = parse24(value);

  // Ensure the current (possibly off-step) minute is selectable.
  const minutes = [...new Set([...STEP_MINUTES.map((m) => String(m).padStart(2, '0')), ...(minute ? [minute] : [])])]
    .sort((a, b) => parseInt(a, 10) - parseInt(b, 10));

  const emit = (nh, nm, np) => {
    const H = nh || '12';
    const M = nm || '00';
    const P = np || 'AM';
    onChange(to24(H, M, P));
  };

  return (
    <div className="flex items-center gap-1.5" data-testid={testid}>
      <Select value={h12} onValueChange={(v) => emit(v, minute, period)}>
        <SelectTrigger className="h-11 w-[68px]" aria-label="Hour"><SelectValue placeholder="--" /></SelectTrigger>
        <SelectContent className="max-h-64">
          {HOURS.map((h) => <SelectItem key={h} value={String(h)}>{h}</SelectItem>)}
        </SelectContent>
      </Select>
      <span className="text-muted-foreground">:</span>
      <Select value={minute} onValueChange={(v) => emit(h12 || '12', v, period)}>
        <SelectTrigger className="h-11 w-[74px]" aria-label="Minute"><SelectValue placeholder="--" /></SelectTrigger>
        <SelectContent className="max-h-64">
          {minutes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={period} onValueChange={(v) => emit(h12 || '12', minute, v)}>
        <SelectTrigger className="h-11 w-[74px]" aria-label="AM or PM"><SelectValue placeholder="--" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="AM">AM</SelectItem>
          <SelectItem value="PM">PM</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};
